import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, concatMap, mergeMap, switchMap, debounceTime } from 'rxjs/operators';
import { Observable, EMPTY, of } from 'rxjs';
import { ActivityPlanningInputActions } from './activity-planning-input.actions';
import { ActivityPlanningTreeService } from '../../service/activity-planning-tree.service';


@Injectable()
export class ActivityPlanningInputEffects {

  loadActivityPlanningInput$ = createEffect(
    (rs: ActivityPlanningTreeService = inject(ActivityPlanningTreeService)) => {
      return this.actions$.pipe(

        ofType(ActivityPlanningInputActions.loadActivityPlanningTree),
        debounceTime(50),
        switchMap(({ dashboardId, page, pageSize, query }) =>
          rs.getActivityPlanningTree(dashboardId, page, pageSize, query).pipe(
            map((data) => {
              return ActivityPlanningInputActions.loadActivityPlanningTreeSuccess({
                data
              });
            }),
            catchError((error) => of(ActivityPlanningInputActions.loadActivityPlanningTreeFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  addActivityPlanningInput$ = createEffect(
    (rs: ActivityPlanningTreeService = inject(ActivityPlanningTreeService)) => {
      return this.actions$.pipe(
        ofType(ActivityPlanningInputActions.addLevel3Activity),
        mergeMap(({ input }) =>
          rs.addActivityPlanning(input).pipe(
            map((node) => {
              return ActivityPlanningInputActions.addLevel3ActivitySuccess({
                node
              });
            }),
            catchError((error) => of(ActivityPlanningInputActions.addLevel3ActivityFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  updateDemand$ = createEffect(
    (rs: ActivityPlanningTreeService = inject(ActivityPlanningTreeService)) => {
      return this.actions$.pipe(
        ofType(ActivityPlanningInputActions.updateDemand),
        mergeMap(({ input }) =>
          rs.updateDemand(input).pipe(
            map((update) => {
              return ActivityPlanningInputActions.updateDemandSuccess({
                update
              });
            }),
            catchError((error) => of(ActivityPlanningInputActions.updateDemandFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  deleteLevel3Activity$ = createEffect(
    (rs: ActivityPlanningTreeService = inject(ActivityPlanningTreeService)) => {
      return this.actions$.pipe(
        ofType(ActivityPlanningInputActions.deleteLevel3Activity),
        mergeMap(({ input }) =>
          rs.deleteLevel3Activity(input).pipe(
            map((data) => {
              return ActivityPlanningInputActions.deleteLevel3ActivitySuccess({ data });
            }),
            catchError((error) => of(ActivityPlanningInputActions.deleteLevel3ActivityFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  updateLevel3Name$ = createEffect(
    (rs: ActivityPlanningTreeService = inject(ActivityPlanningTreeService)) => {
      return this.actions$.pipe(
        ofType(ActivityPlanningInputActions.updateLevel3Name),
        mergeMap(({ input }) =>
          rs.updateLevel3Name(input).pipe(
            map((data) => ActivityPlanningInputActions.updateLevel3NameSuccess({ data })),
            catchError((error) => of(ActivityPlanningInputActions.updateLevel3NameFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  downloadActivityPlanningExcel$ = createEffect(
    (rs: ActivityPlanningTreeService = inject(ActivityPlanningTreeService)) => {
      return this.actions$.pipe(
        ofType(ActivityPlanningInputActions.downloadActivityPlanningExcel),
        mergeMap(({ dashboardId, query }) =>
          rs.downloadExcel(dashboardId, query).pipe(
            map((res) => ActivityPlanningInputActions.downloadActivityPlanningExcelSuccess({
              file: { content: res?.data?.content as string, filename: res?.data?.filename as string }
            })),
            catchError((error) => of(ActivityPlanningInputActions.downloadActivityPlanningExcelFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  constructor(private actions$: Actions) { }
}
